<template>
    <div :class="['d-flex flex-column h-100', isRatesTable]">
        <metainfo>
            <template v-slot:title="{ content }">{{ content }}</template>
        </metainfo>

        <component
            v-if="(isGlobalDataReady && !hideHeader) || headerName == 'WizardHeader'"
            v-bind:is="headerName"
            :isStaticPage="isStaticPage"
        ></component>

        <div v-if="isGlobalDataReady && headerName == 'WizardHeader'" class="d-none d-lg-block">
            <progress-bar></progress-bar>
        </div>

        <div class="legal-header-parent">
            <LegalHeaderBar v-if="!isRatesTable && headerName !== 'WizardHeader'" class="legal-header-bar" />

            <!-- Error page can load without global page data -->
            <div v-if="showErrorPage">
                <ErrorPageContent />
            </div>

            <div v-else-if="isPageDataReady">
                <slot></slot>
            </div>
        </div>

        <component
            v-if="isGlobalDataReady && !hideFooter && !showErrorPage"
            v-bind:is="footerName"
            :isRatesTable="isRatesTable"
        ></component>
    </div>
</template>

<script>
import { ref, watchEffect } from 'vue'
import { useRouter, useRoute } from 'vue-router'
import { useStore } from 'vuex'
import { useMeta } from 'vue-meta'

// lodash components
import size from 'lodash/size'
import debounce from 'lodash/debounce'
import find from 'lodash/find'
import startsWith from 'lodash/startsWith'

export default {
    props: ['header', 'footer', 'errorPage', 'isAgentPage','isCrmPage', 'isRatesTable', 'isStaticPage'],
    setup() {
        const metaTitle = ref('metaTitle')
        const metaDescription = ref('metaDescription')

        const router = useRouter()
        const route = useRoute()
        const store = useStore()
        const vueMeta = useMeta({})

        watchEffect(() => {
            if (metaTitle.value && metaTitle.value !== 'metaTitle') {
                vueMeta.meta.title = metaTitle.value
            }

            if (metaDescription.value && metaDescription.value !== 'metaDescription') {
                vueMeta.meta.description = metaDescription.value
            }
        })

        return {
            router,
            route,
            store,
            metaTitle,
            metaDescription,
        }
    },
    created() {
        if (window.TERRITORY !== process.env.TERRITORY) {
            this.store.commit('showErrorPage', {
                title: 'Build Error',
                description: 'Territory mismatch! Re-run yarn',
            })
        }

        this.emitter.on('meta:title', title => {
            this.metaTitle = title
        })

        this.emitter.on('meta:description', description => {
            this.metaDescription = description
        })

        this.emitter.on('insertOnetrust', () => {
            this.injectOneTrustScript()
        })

        this.injectOneTrustScript()
    },
    computed: {
        showErrorPage: function () {
            return this.store.state.global.data.showErrorPage
        },
        headerName: function () {
            if (this.header === false) {
                return false
            }

            if (this.header === 'wizard') {
                return 'WizardHeader'
            } else {
                return 'PrimaryHeader'
            }
        },
        hideFooter: function () {
            const hideRtwFooter =
                this.isRatesTable &&
                (parseInt(this.store.state.global.page?.toggle_rate_table_footer) === 1 ||
                    parseInt(this.store.state.global.page?.toggle_all_rate_table_filters) === 1)

            if(this.isCrmPage || this.isAgentPage){
                return false
            }
            return !!(parseInt(this.themeSetting('hide_footer')) === 1  || hideRtwFooter);
        },
        hideHeader: function () {
            if(this.isCrmPage || this.isAgentPage){
                return false
            }
            return !!(parseInt(this.themeSetting('hide_header')) === 1 || this.isRatesTable);
        },
        footerName: function () {
            if (this.footer === false) {
                return false
            }

            if (this.isRatesTable) {
                return 'RatesTableFooter'
            }

            return 'PrimaryFooter'
        },
        isGlobalDataReady: function () {
            // has global data api received results
            return size(this.store.state.global.data.theme_settings)
        },
        isPageDataReady: function () {
            let ready = size(this.store.state.global.page)

            if (ready && startsWith(this.router.currentRoute.name, 'apply')) {
                ready = this.store.state.apply.applyRoutesLoaded
            }

            // has global data api received results
            return ready
        },
        showAdvertiserDisclosure: function () {
            return (
                process.env.TERRITORY === 'usa' &&
                !this.isAgentPage &&
                this.store.state.global.data.LEGAL_TEXT_LABEL &&
                this.store.state.global.data.LEGAL_TEXT_LINK
            )
        },
        oneTrust: function () {
            return this.store.state.global.data.one_trust
        },
        generalLoaderVisible() {
            return this.store.state.global.data.showGeneralLoader
        },
    },
    methods: {
        isNumeric: function (value) {
            return /^-?\d+$/.test(value)
        },
        orderPreference: function (field, queryKey, query) {
            if (field.value === null) {
                return queryKey
            }

            let order = {
                annual_income: ['monthly_income', 'annual_income'],
            }

            let adjustedKey = find(order[field.mappedKey], (value, key) => {
                return query[value]
            })

            return adjustedKey || queryKey
        },
        injectOneTrustScript: debounce(function () {
            if (!this.oneTrust || !this.oneTrust.url || !this.oneTrust.token || this.isRatesTable) {
                return
            }

            this.removeOneTrustAssets()

            let script = document.createElement('script')
            script.type = 'text/javascript'
            script.innerHTML = 'function OptanonWrapper() { }'
            document.getElementsByTagName('head')[0].prepend(script)

            script = document.createElement('script')
            script.type = 'text/javascript'
            script.charset = 'UTF-8'
            script.dataset.domainScript = this.oneTrust.token
            script.src = this.oneTrust.url
            document.getElementsByTagName('head')[0].prepend(script)

            let comment = document.createComment('OneTrust Cookies Consent Notice start')
            document.getElementsByTagName('head')[0].prepend(comment)
        }, 100),
        removeOneTrustAssets: function () {
            // Remove the script injected by us
            const oneTrustDomainScript = document.querySelectorAll('[data-domain-script]')
            if (oneTrustDomainScript[0]) {
                oneTrustDomainScript[0].remove(oneTrustDomainScript)
            }

            // Remove third-party injected script
            const oneTrustStyle = document.querySelectorAll('#onetrust-style')
            if (oneTrustStyle[0]) {
                oneTrustStyle[0].remove(oneTrustStyle)
            }

            const oneTrustSDK = document.querySelectorAll('#onetrust-consent-sdk')
            if (oneTrustSDK[0]) {
                oneTrustSDK[0].remove(oneTrustSDK)
            }
        },
    },
}
</script>

<style lang="scss" scoped>
@import '~scss/mixins/rem';
@import '~scss/settings/responsive';

.legal-header-parent {
    position: relative;
}

.legal-header-bar {
    position: absolute;
    top: 0;
    right: 0;
    padding: 0 15px;
    z-index: 10;

    @media screen and (min-width: $bp-large) {
        padding: 0 75px;
    }
}
</style>
